import React, { PureComponent } from 'react';
import _ from 'lodash';
import { array, object } from 'prop-types';
import { Provider } from 'react-redux';
import MediaQuery from 'react-responsive';
import Waypoint from 'react-waypoint';
import classNames from 'classnames';
import { api as collectApi } from '@fiverr-private/collect';
import { getContext, I18n } from '@fiverr-private/fiverr_context';
import { DEFAULT_LOCALE } from '@fiverr-private/fiverr_context/lib/constants';

import createGigStore from '../../store';
import { getPackageById, isSinglePackage, isTriplePackage } from '../../reducers/packages';
import { updateCollections } from '../../actions/collections';
import { updateOnlineStatus } from '../../actions/isOnline';
import { managePageViews } from '../../utils/recentlyViewedPages';
import eventsReporter from '../../utils/reporter';
import { VARIANTS } from '../../types/variants';

import { getSellerLanguageParams } from '../../utils/sellerLanguage';
import scrollToComponent from '../../utils/scrollToComponent';
import BrowserDetector from '../../utils/browserDetector';
import fetchIsOnline from '../../utils/fetchIsOnline';

import {
    ABOUT_SELLER,
    DESCRIPTION,
    FAQ,
    ORDER_DETAILS,
    OVERVIEW,
    PACKAGES_TABLE,
    RECOMMENDATIONS,
    REVIEWS
} from '../../utils/pageSections';
import {
    validateDescription,
    validateFaq,
    validateOrderDetails,
    validateOverview,
    validatePackagesTable,
    validateReviews,
    validateSellerCard
} from '../../utils/mainComponentsValidator';
import BiEvents from '../../utils/biEvents';
import GigCurrencyConverter from '../../utils/gigCurrencyConverter';
import { MP_SHOW_PAGE_NAME } from '../../utils/mixPanelEvents';
import { BQ_SELLER_BUYER_SPEAK_SAME_LANGUAGE, BQ_PAGE_TRANSLATED_BY_EXTENSION } from '../../utils/bigQueryEvents';
import { isGigApproved, isGigPaused } from '../../utils/gigStatus';
import { isFiverrChoice } from '../../utils/fiverrChoice';
import { addChoiceView, removeChoiceView } from '../../utils/fiverrChoice/choiceViewLocalStorageManager';
import { isLoggedInUser } from '../../utils/isLoggedInUser';
import Logger from '../../utils/logger';
import PathfinderWrapper from '../../utils/pathfinderWrapper';
import { BASIC_PACKAGE_ID } from '../../utils/packagesUtils/constants';
import { GIG_SUB_HEADER } from '../../utils/events';
import {
    GTM_EVENT_NAME,
    MEDIA_GRID_MD,
    SCROLL_DURATION,
    SCROLL_OFFSET,
    WAYPOINT_OFFSET
} from '../../utils/constants';
import { MIXPANEL_EVENT_TYPES } from '../../types/mixpanelEventTypes';
import isInCarouselObserverTest from '../../utils/experiments/carouselObserver';
import { inCarouselExternalVisitsExperiment } from '../../utils/experiments/carouselExternalVisits';
import { inMobileStickyExperience } from '../../utils/experiments/mobileStickyCta';
import { isValidExternalHttpUrl } from '../../utils/url';
import isIntersectionObserverDefined from '../../utils/isIntersectionObserverDefined';

import SellerCard from '../SellerCard';
import Description from '../Description';
import Workflow from '../Workflow';
import Faq from '../Faq';
import Reviews from '../Reviews';
import Tags from '../Tags';
import PackagesTable from '../PackagesTable';
import OrderDetails from '../OrderDetails';
import TopNav from '../TopNav';
import ShareYourGig from '../ShareYourGig';
import Gallery from '../Gallery';
import Overview from '../Overview';
import Portfolio from '../Portfolio';
import OutOfOffice from '../OutOfOffice';
import SimilarGigs from '../SimilarGigs';
import RecommendedInSc from '../RecommendedInSc';
import RecommendedInScFromExternalVisits from '../RecommendedInScFromExternalVisits';
import MobileStickyCta from '../MobileStickyCta';
import OtherGigsBy from '../OtherGigsBy';
import RecentlyViewedGigs from '../RecentlyViewedGigsWrapper';
import Disclaimer from '../Disclaimer';
import MoreServices from '../MoreServices';
import PausedGig from '../PausedGig';
import ProVideo from '../ProVideo';
import ProBanner from '../ProBanner';
import ProCertificateBanner from '../ProCertificateBanner';
import ReviewsSnippet from '../ReviewsSnippet';
import UGCTranslateButton from '../UGCTranslateButton';
import Seo from '../Seo';
import CouponBanner from '../CouponBanner';
import LimitedEditionBanner from '../LimitedEdition/Banner';
import Sidebar from '../Sidebar';
import { GOOGLE_TRANSLATE_EXTENSION_NAME, GOOGLE_TRANSLATE_CLASS_NAMES } from './constants';

import './style.scss';

class GigPage extends PureComponent {

    constructor(props) {
        super(props);

        const {
            general,
            overview
        } = this.props;

        if (!general) {
            // If general params is missing throw an error and don't render the page
            throw Error('Missing general data');
        }

        this.state = {
            sectionKey: OVERVIEW,
            prevSectionKey: OVERVIEW,
            useWaypoints: true,
            hideRecommended: false,
            showTranslateBtnInNav: false,
            observedCarouselsBarrier: false
        };

        this.scrollToSection = this.scrollToSection.bind(this);
        this.onLeaveWaypoint = this.onLeaveWaypoint.bind(this);
        this.onEnterWaypoint = this.onEnterWaypoint.bind(this);
        this.wrapChildren = this.wrapChildren.bind(this);
        this.onEmptyRecommendedForYouGigs = this.onEmptyRecommendedForYouGigs.bind(this);
        this.onComparePackagesClick = this.onComparePackagesClick.bind(this);

        this.store = createGigStore(this.props);
        this.useStickyLayout = this.shouldUseStickyLayout();

        // used for unwrapping/rewrapping
        this.orphans = [];
        this.displayContents = true;
        this.isMediaMd = true;

        const packagesCount = _.get(this.store.getState(), 'packages.packageList.length', 3);

        const { locale, browserLanguage, countryCode } = getContext();
        this.sellerLanguageParams = getSellerLanguageParams({
            buyer: { locale, browserLanguage, countryCode },
            seller: overview.seller
        });

        this.biEvents = new BiEvents({ general, packagesCount });
        this.logger = Logger.init();
        this.pathfinderWrapper = new PathfinderWrapper(this.logger);
    }

    getChildContext() {
        const {
            general,
            currency,
            choiceEligibilities,
            limitedEdition,
            rollouts,
            currentUser
        } = this.props;

        return {
            biEvents: this.biEvents,
            general,
            logger: this.logger,
            currencyConverter: new GigCurrencyConverter(currency),
            pathfinderWrapper: this.pathfinderWrapper,
            choiceEligibilities,
            limitedEdition,
            rollouts,
            currentUser
        };
    }

    componentDidMount() {
        this.setDisplayContentsSupport();
        this.wrapChildren();
        this.reportPageView();
        this.gtmReport();
        this.updateChoiceViewStorage();
        this.initCarouselsObserver();
        this.initBrowserDetector();
        this.fetchCollections();
        this.reportBuyerSpeakSellerLanguage();
        this.observeTranslationExtensionUsage();
        this.enrichReporter();
        this.manageRecentlyViewedPages();
        this.fetchOnlineStatus();
    }

    manageRecentlyViewedPages() {
        const { rollouts, general } = this.props;
        const { gigId } = general;

        managePageViews(rollouts, gigId, this.pathfinderWrapper);
    }

    setDisplayContentsSupport() {
        if (window.CSS && window.CSS.supports) {
            this.displayContents = CSS.supports('display: contents');
        } else if (window.supportsCSS) { // Opera's old version of css detection
            this.displayContents = window.supportsCSS('display:contents');
        } else {
            this.displayContents = false;
        }
    }

    reportPageView() {
        const { packages } = this.store.getState();
        const { buyerAssumedLanguage, sellerCountryCode, sellerValidatedLanguages } = this.sellerLanguageParams;
        const sellerLanguagesStringArray = sellerValidatedLanguages.map((languageObj) => `${languageObj.name} - ${languageObj.level}`);
        const minPackagePrice = getPackageById(packages, BASIC_PACKAGE_ID)?.price;

        this.biEvents.sendMixPanelEvent({
            mpEventType: MIXPANEL_EVENT_TYPES.PAGE_VIEW,
            eventName: MP_SHOW_PAGE_NAME,
            params: {
                buyerAssumedLanguage,
                sellerCountryCode,
                sellerValidatedLanguages: sellerLanguagesStringArray,
                hasWorkProcess: this.hasWorkProcess,
                minPackagePrice: minPackagePrice / 100
            }
        });
    }

    reportBuyerSpeakSellerLanguage() {
        const { shouldShowSellerLanguage, commonLanguage } = this.sellerLanguageParams;

        if (shouldShowSellerLanguage) {
            this.biEvents.sendBigQueryEvent({
                eventName: BQ_SELLER_BUYER_SPEAK_SAME_LANGUAGE,
                params: {
                    commonLanguage
                }
            });
        }
    }

    observeTranslationExtensionUsage() {
        if (window && window.MutationObserver) {
            const observer = new MutationObserver((mutationList) => {
                mutationList.forEach(({ type, attributeName, target }) => {
                    if (type === 'attributes' && attributeName === 'class') {
                        this.detectTranslationExtension(target);
                    }
                });
            });

            observer.observe(window.document.querySelector('html'), {
                attributes: true,
                attributeFilter: ['class'],
                childList: false,
                subtree: false
            });
        }
    }

    detectTranslationExtension(node) {
        const nodeClassNamesArr = [...node.classList];
        const isTranslationExtensionDetected = GOOGLE_TRANSLATE_CLASS_NAMES.some((className) => nodeClassNamesArr.includes(className));
        if (isTranslationExtensionDetected) {
            const { locale } = getContext();

            this.biEvents.sendBigQueryEvent({
                eventName: BQ_PAGE_TRANSLATED_BY_EXTENSION,
                params: {
                    locale,
                    translationSource: GOOGLE_TRANSLATE_EXTENSION_NAME
                }
            });
        }
    }

    enrichReporter() {
        const { general } = this.props;
        const { gigId } = general;
        const enrichment = { gig: { id: gigId } };

        eventsReporter.enrich(enrichment);
    }

    gtmReport() {
        if (!window.dataLayer) {
            return;
        }

        const { general } = this.props;
        const { gigId, gigTitle, isPro, categorySlug, subCategorySlug, nestedSubCategorySlug } = general;
        const { packages } = this.store.getState();
        const basicPackage = getPackageById(packages, BASIC_PACKAGE_ID);

        window.dataLayer.push({
            event: GTM_EVENT_NAME,
            gigId,
            gigTitle,
            isPro,
            categorySlug,
            subCategorySlug,
            nestedSubCategorySlug,
            basePrice: basicPackage ? basicPackage.price : 0
        });

        if (this.hasWorkProcess) {
            window.dataLayer.push({ event: 'workflow-gig-page-recording-requested' });
        }
    }

    updateChoiceViewStorage() {
        const { general, choiceEligibilities } = this.props;
        const { gigId } = general;
        const { queryParameters, abTests } = getContext();

        if (!isLoggedInUser()) {
            return;
        }

        isFiverrChoice({ queryParameters, abTests, choiceEligibilities }) ? addChoiceView(gigId) : removeChoiceView(gigId);
    }

    initCarouselsObserver() {
        if (!isIntersectionObserverDefined()) {
            return;
        }

        const observeTarget = this.carouselsBarrier;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].intersectionRatio <= 0) {
                return;
            }
            disconnect();
        });

        const disconnect = () => {
            this.setState({ observedCarouselsBarrier: true });
            observer.unobserve(observeTarget);
        };

        observer.observe(observeTarget);

        // This is for the case when a page refresh takes place when the
        // the scroll isn't at the top which leads to a native scroll
        // operation by the browser. In that case when the component is mounted
        // the scroll is still at the top which leads to observedCarouselsBarrier
        // staying as false
        const OBSERVER_TIMEOUT = 2000;
        setTimeout(() => {
            if (this.passedCarouselsAnchor()) {
                disconnect();
            }
        }, OBSERVER_TIMEOUT);
    }

    initBrowserDetector() {
        BrowserDetector.init();
    }

    passedCarouselsAnchor() {
        if (!(window && this.carouselsBarrier)) {
            return false;
        }

        return window.scrollY > this.carouselsBarrier.offsetTop + this.carouselsBarrier.offsetHeight;
    }

    scrollToSection({ sectionKey }) {

        const refsObj = {};

        refsObj[OVERVIEW] = this.sectionOverview;
        refsObj[DESCRIPTION] = this.sectionDescription;
        refsObj[RECOMMENDATIONS] = this.sectionRecommendations;
        refsObj[FAQ] = this.sectionFaq;
        refsObj[REVIEWS] = this.sectionReviews;
        refsObj[ORDER_DETAILS] = this.sectionOrderDetails;
        refsObj[ABOUT_SELLER] = this.sectionAboutSeller;
        refsObj[PACKAGES_TABLE] = this.sectionPackagesTable;

        const currentSection = this.state.sectionKey;

        if ((refsObj[sectionKey]) && (sectionKey !== currentSection)) {

            this.setState({
                sectionKey,
                useWaypoints: false,
                prevSectionKey: currentSection
            }, () => {

                const scroller = scrollToComponent(refsObj[sectionKey], { offset: SCROLL_OFFSET, align: 'top', duration: SCROLL_DURATION });

                // we need a timeout here, otherwise the waypoints fire
                // right at the end of the scroll
                scroller.onComplete(() => {
                    setTimeout(() => {
                        this.setState({
                            useWaypoints: true
                        });
                    }, 50);
                });

            });

        }
    }

    onEnterWaypoint(newSection, data) {

        const currentSection = this.state.sectionKey;
        const useWaypoints = this.state.useWaypoints;
        const scrollingUp = (data.currentPosition === 'inside') && (data.previousPosition === 'above');

        if ((useWaypoints) && (newSection !== currentSection) && (scrollingUp)) {
            this.setState({
                sectionKey: newSection,
                prevSectionKey: currentSection
            });
        }
    }

    onLeaveWaypoint(newSection, data) {

        const currentSection = this.state.sectionKey;
        const useWaypoints = this.state.useWaypoints;

        if ((useWaypoints) && (newSection !== currentSection)) {

            if (data.waypointTop < WAYPOINT_OFFSET) {
                this.setState({
                    sectionKey: newSection,
                    prevSectionKey: currentSection
                });
            } else {
                this.setState({
                    sectionKey: currentSection
                });
            }
        }

    }

    isPro() {
        const { general } = this.props;

        return general.isPro;
    }

    isPageOwner() {
        const { sellerId } = this.props.general;
        const { userId } = getContext();

        return sellerId === userId;
    }

    getPageSectionsList() {
        const {
            general,
            overview,
            description,
            faq,
            reviews,
            sellerCard
        } = this.props;

        const { hideRecommended } = this.state;
        const { isStudio } = general;

        return [
            { sectionKey: OVERVIEW, validated: validateOverview({ overview }) },
            { sectionKey: DESCRIPTION, validated: validateDescription({ description }) },
            { sectionKey: FAQ, validated: validateFaq({ faq }) },
            { sectionKey: REVIEWS, validated: validateReviews({ reviews }) },
            { sectionKey: ABOUT_SELLER, validated: !isStudio && validateSellerCard({ sellerCard }) },
            { sectionKey: PACKAGES_TABLE, validated: this.shouldRenderPackagesTable() },
            { sectionKey: ORDER_DETAILS, validated: this.shouldRenderOrderDetails() },
            { sectionKey: RECOMMENDATIONS, validated: !hideRecommended && this.shouldRenderSimilarGigs() }
        ];
    }

    canWrapUnwrapChildren() {
        return this.sidebar && this.main && !this.displayContents;
    }

    wrapChildren() {
        if (this.canWrapUnwrapChildren()) {
            this.isMediaMd ? this.rewrapChildren() : this.unwrapChildren();
        }
    }

    unwrapChildren() {
        if (!this.sidebar.firstChild) {
            return;
        }

        this.orphans = [];
        while (this.sidebar.firstChild) {
            this.orphans.push(this.sidebar.firstChild);
            this.main.insertBefore(this.sidebar.firstChild, this.main.firstChild);
        }

    }

    rewrapChildren() {
        for (let i=0; i<this.orphans.length; i++) {
            this.sidebar.appendChild(this.orphans[i]);
        }
    }

    onEmptyRecommendedForYouGigs() {
        this.setState({
            hideRecommended: true
        });
    }

    onComparePackagesClick() {
        this.scrollToSection({ sectionKey: PACKAGES_TABLE });
    }

    shouldRenderPackagesTable() {
        const { packages } = this.store.getState();

        return validatePackagesTable({ isTriplePackage: isTriplePackage(packages) });
    }

    shouldRenderTopNav(){
        return !this.shouldRenderRecommendedInScFromExternalVisits();
    }

    shouldRenderRecommendedInScFromExternalVisits() {
        const { abTests, referrer, isMobile } = getContext();
        const { general } = this.props;
        const { gigStatus, isStudio } = general;
        const isPageOwner = this.isPageOwner();
        const isApproved = isGigApproved(gigStatus);
        const emptyOrExternalReferrer = !referrer || isValidExternalHttpUrl(referrer);
        const inExperiment = inCarouselExternalVisitsExperiment(abTests);

        return !isPageOwner && isApproved && !isStudio && !isMobile && emptyOrExternalReferrer && inExperiment;
    }

    shouldRenderMoreServices() {
        const { general } = this.props;
        const { isStudio } = general;

        return !isStudio;
    }

    shouldRenderOutOfOffice() {
        const { general } = this.props;
        const { isOnVacation, isStudio } = general;

        return isOnVacation && !isStudio;
    }

    shouldRenderOrderDetails() {
        const { packages } = this.store.getState();
        const { general } = this.props;
        const { isLimitedEdition } = general;

        return !isLimitedEdition && validateOrderDetails({ isSinglePackage: isSinglePackage(packages) });
    }

    shouldRenderSimilarGigs() {
        const { general } = this.props;
        const { gigStatus, isStudio, isLimitedEdition } = general;
        const isPageOwner = this.isPageOwner();
        const isApproved = isGigApproved(gigStatus);
        const isPro = this.isPro();

        return !isPro && !isPageOwner && isApproved && !isStudio && !isLimitedEdition;
    }

    shouldRenderRecommendedInSc() {
        const { general } = this.props;
        const { gigStatus, isStudio } = general;
        const isPageOwner = this.isPageOwner();
        const isApproved = isGigApproved(gigStatus);
        const shouldRenderRecommendedInScFromExternalVisits = this.shouldRenderRecommendedInScFromExternalVisits();

        return !isPageOwner && isApproved && !isStudio && !shouldRenderRecommendedInScFromExternalVisits;
    }

    shouldRenderRecentlyGigs() {
        const isPageOwner = this.isPageOwner();
        const isPro = this.isPro();

        return !isPro && !isPageOwner;
    }

    shouldRenderProBanner() {
        const isPro = this.isPro();
        const { isLimitedEdition } = this.props.general;

        return isPro && !isLimitedEdition;
    }

    shouldRenderLimitedEditionBanner() {
        const { isLimitedEdition } = this.props.general;

        return isLimitedEdition;
    }

    shouldRenderBottomCarousels() {
        const { isLimitedEdition } = this.props.general;

        return !isLimitedEdition;
    }

    shouldUseStickyLayout() {
        const { general } = this.props;

        return !general.isStudio;
    }

    shouldRenderReviews() {
        const { isLimitedEdition } = this.props.general;

        return !isLimitedEdition;
    }

    shouldRenderProVideo() {
        const { isLimitedEdition } = this.props.general;
        const isPro = this.isPro();

        return isPro && !isLimitedEdition;
    }

    shouldRenderSellerLanguage() {
        const { shouldShowSellerLanguage } = this.sellerLanguageParams;

        return shouldShowSellerLanguage;
    }

    inMobileStickyExperience() {
        const context = getContext();

        return inMobileStickyExperience({
            context,
            general: this.props.general,
            variations: Object.values(VARIANTS)
        });
    }

    get hasWorkProcess() {
        const { workflow } = this.props;
        return !_.isEmpty(workflow);
    }

    shouldFetchCarousels() {
        const { abTests } = getContext();
        const inCarouselObserverTest = isInCarouselObserverTest(abTests);
        const { observedCarouselsBarrier } = this.state;

        return !inCarouselObserverTest ||
            !isIntersectionObserverDefined() ||
            observedCarouselsBarrier ||
            this.passedCarouselsAnchor();
    }

    async fetchCollections() {
        if (isLoggedInUser()) {
            const { collections } = await collectApi.getCollections();
            this.store.dispatch(updateCollections(collections));
        }
    }

    async fetchOnlineStatus() {
        const isOnline = await fetchIsOnline({
            pathfinderWrapper: this.pathfinderWrapper,
            logger: this.logger
        });

        this.store.dispatch(updateOnlineStatus(isOnline));
    }

    render() {

        const {
            description,
            reviews,
            sellerCard,
            tags,
            topNav,
            gallery,
            overview,
            portfolio,
            customOrder,
            outOfOffice,
            moreServices,
            otherGigs,
            general,
            studio,
            seo,
            faq,
            coupon,
            repeatScore,
            workflow,
            notableClients
        } = this.props;

        const {
            sectionKey,
            showTranslateBtnInNav
        } = this.state;

        const { gigStatus, traffiqed, isStudio, gigId, isTrustedUser } = general;
        const { pathParameters, isTouch } = getContext();
        const { username, slug } = pathParameters;
        const { info, services } = studio;
        const { metadataAttributes: descriptionMetadata } = description;

        const isLoggedIn = isLoggedInUser();
        const isPageOwner = this.isPageOwner();
        const isPaused = isGigPaused(gigStatus, traffiqed);
        const isPro = this.isPro();

        const shouldRenderPackagesTable = this.shouldRenderPackagesTable();
        const shouldRenderMoreServices = this.shouldRenderMoreServices();
        const shouldRenderOutOfOffice = this.shouldRenderOutOfOffice();
        const shouldRenderProBanner = this.shouldRenderProBanner();
        const shouldRenderReviews = this.shouldRenderReviews();
        const shouldRenderProVideo = this.shouldRenderProVideo();
        const shouldRenderLimitedEditionBanner = this.shouldRenderLimitedEditionBanner();
        const shouldRenderSellerLanguage = this.shouldRenderSellerLanguage();

        const mainContainerClassName = 'gig-page';
        const outerClasses = classNames(
            'gig-page-wrapper',
            'responsive',
            {
                'pro-gig': isPro,
                'touch': isTouch
            }
        );

        const shouldFetchCarousel = this.shouldFetchCarousels();
        const isDefaultLocale = getContext().locale === DEFAULT_LOCALE;

        return (
            <Provider store={this.store}>
                {/* TODO remove the data attributes after refactoring first-message-popup into a  v4 package */}
                {/* The reason to the data attributes is that the first-message-popup in v2 requires this data */}
                <div className={outerClasses} data-gig-id={gigId} data-username={username} data-slug={slug}>
                    <Seo seo={seo} faq={faq}/>
                    <MediaQuery query={`(min-width: ${MEDIA_GRID_MD})`}>
                        {(matches) => {
                            this.isMediaMd = matches;
                            this.wrapChildren();
                            return false;
                        }}
                    </MediaQuery>

                    {this.shouldRenderTopNav() &&
                    <TopNav topNav={topNav}
                        showUGCTranslateBtn={showTranslateBtnInNav && !isDefaultLocale}
                        pageSectionsList={this.getPageSectionsList()}
                        scrollToSection={this.scrollToSection}
                        isPageOwner={isPageOwner}
                        isTrustedUser={isTrustedUser}
                        selSection={sectionKey}/>
                    }

                    <div className="gig-page-top">
                        {isPageOwner && <ShareYourGig/>}
                        {!isPageOwner && isPaused && <PausedGig/>}
                        {coupon && <CouponBanner coupon={coupon}/>}
                    </div>

                    {this.shouldRenderRecommendedInScFromExternalVisits() &&
                    <div className="top-carousels-container">
                        <RecommendedInScFromExternalVisits isLoggedIn={isLoggedIn}
                            isTouch={isTouch}
                            shouldFetchCarousel={shouldFetchCarousel}/>
                    </div>
                    }

                    {this.inMobileStickyExperience() &&
                        <MobileStickyCta overview={overview}
                            customOrder={customOrder}/>
                    }

                    <div className={classNames(mainContainerClassName, { 'coupon-banner': !!coupon })}>
                        <Sidebar setRef={(ele) => { this.sidebar = ele; }}
                            onComparePackagesClick={this.onComparePackagesClick}
                            useStickyLayout={this.useStickyLayout}
                            containerClassName={mainContainerClassName}
                            customOrder={customOrder}
                            studio={studio}/>

                        <div className="main" ref={(ele) => { this.main = ele; }}>

                            {shouldRenderProBanner && <ProBanner/>}
                            {shouldRenderLimitedEditionBanner && <LimitedEditionBanner/>}
                            <Waypoint onEnter={((data) => this.onEnterWaypoint(OVERVIEW, data))}
                                onLeave={((data) => this.onLeaveWaypoint(OVERVIEW, data))}
                                topOffset={WAYPOINT_OFFSET}/>
                            {shouldRenderOutOfOffice &&
                                <OutOfOffice outOfOffice={outOfOffice} isPageOwner={isPageOwner}/>
                            }
                            <Overview overview={overview}
                                studioInfo={info}
                                ref={(ele) => { this.sectionOverview = ele; }}
                                scrollToSection={this.scrollToSection}
                                useStickyLayout={this.useStickyLayout}
                                shouldRenderSellerLanguage={shouldRenderSellerLanguage}
                                repeatScore={repeatScore}
                                notableClients={notableClients}/>

                            <Waypoint onEnter={() => this.setState({ showTranslateBtnInNav: false })}
                                onLeave={() => this.setState({ showTranslateBtnInNav: true })}
                                topOffset={WAYPOINT_OFFSET}/>

                            {!isDefaultLocale &&
                            <UGCTranslateButton componentName={GIG_SUB_HEADER} useStickyTranslation={true}/>
                            }

                            <Gallery gallery={gallery}/>
                            <Waypoint onEnter={((data) => this.onEnterWaypoint(DESCRIPTION, data))}
                                onLeave={((data) => this.onLeaveWaypoint(DESCRIPTION, data))}
                                topOffset={WAYPOINT_OFFSET}/>
                            { shouldRenderReviews &&
                                <ReviewsSnippet reviews={reviews} scrollToSection={this.scrollToSection}/>
                            }

                            <span className="carousels-barrier" ref={(ele) => { this.carouselsBarrier = ele; }}/>

                            <Description metadataAttributes={descriptionMetadata}
                                ref={(ele) => { this.sectionDescription = ele; }}/>

                            <Workflow workflow={workflow}/>

                            {!isStudio && <Disclaimer/>}

                            {shouldRenderProBanner && <ProCertificateBanner/>}

                            {!isStudio &&
                                <>
                                    <Waypoint onEnter={((data) => this.onEnterWaypoint(ABOUT_SELLER, data))}
                                        onLeave={((data) => this.onLeaveWaypoint(ABOUT_SELLER, data))}
                                        topOffset={WAYPOINT_OFFSET}/>
                                    <h2 className="section-title about-the-seller" ref={(ele) => { this.sectionAboutSeller = ele; }}>
                                        <I18n k={'gig_page_perseus.general.about_the_seller'}/>
                                    </h2>
                                    <SellerCard {...sellerCard} shouldRenderSellerLanguage={shouldRenderSellerLanguage}
                                        notableClients={notableClients} sellerName={username}/>
                                    <Portfolio portfolio={portfolio} isPageOwner={isPageOwner}/>
                                </>
                            }

                            {shouldRenderPackagesTable &&
                                <MediaQuery query={`(min-width: ${MEDIA_GRID_MD})`}>
                                    <Waypoint onEnter={((data) => this.onEnterWaypoint(PACKAGES_TABLE, data))}
                                        onLeave={((data) => this.onLeaveWaypoint(PACKAGES_TABLE, data))}
                                        topOffset={WAYPOINT_OFFSET}/>
                                    <PackagesTable services={services} ref={(ele) => {this.sectionPackagesTable = ele;}}/>
                                </MediaQuery>
                            }

                            {this.shouldRenderOrderDetails() &&
                            <MediaQuery query={`(min-width: ${MEDIA_GRID_MD})`}>
                                <Waypoint onEnter={((data) => this.onEnterWaypoint(ORDER_DETAILS, data))} onLeave={((data) => this.onLeaveWaypoint(ORDER_DETAILS, data))} topOffset={WAYPOINT_OFFSET}/>
                                <OrderDetails ref={(ele) => { this.sectionOrderDetails = ele; }}/>
                            </MediaQuery>
                            }

                            <Waypoint onEnter={((data) => this.onEnterWaypoint(RECOMMENDATIONS, data))}
                                onLeave={((data) => this.onLeaveWaypoint(RECOMMENDATIONS, data))}
                                topOffset={WAYPOINT_OFFSET}/>
                            {this.shouldRenderSimilarGigs() &&
                                <SimilarGigs ref={(ele) => { this.sectionRecommendations = ele; }}
                                    isLoggedIn={isLoggedIn}
                                    isTouch={isTouch}
                                    onEmptyGigs={this.onEmptyRecommendedForYouGigs}
                                    shouldFetchCarousel={shouldFetchCarousel}/>
                            }

                            {!isStudio && shouldRenderMoreServices &&
                                <MoreServices {...moreServices} sellerName={username}/>
                            }
                            {shouldRenderProVideo && <ProVideo/>}
                            <Waypoint onEnter={((data) => this.onEnterWaypoint(FAQ, data))} onLeave={((data) => this.onLeaveWaypoint(FAQ, data))} topOffset={WAYPOINT_OFFSET}/>
                            <Faq ref={(ele) => { this.sectionFaq = ele; }}/>

                            { shouldRenderReviews &&
                                <>
                                    <Waypoint onEnter={((data) => this.onEnterWaypoint(REVIEWS, data))} onLeave={((data) => this.onLeaveWaypoint(REVIEWS, data))} topOffset={WAYPOINT_OFFSET}/>
                                    <Reviews reviews={reviews} ref={(ele) => { this.sectionReviews = ele; }}/>
                                </>
                            }

                            <Tags tags={tags}/>
                        </div>
                    </div>

                    { this.shouldRenderBottomCarousels() &&
                        <aside className="gig-page-bottom">
                            <div className="max-width-container">

                                <OtherGigsBy otherGigs={otherGigs}
                                    isLoggedIn={isLoggedIn}
                                    isTouch={isTouch}
                                    studioInfo={info}
                                    shouldFetchCarousel={shouldFetchCarousel}/>

                                {this.shouldRenderRecommendedInSc() &&
                                <RecommendedInSc isLoggedIn={isLoggedIn}
                                    isTouch={isTouch}
                                    shouldFetchCarousel={shouldFetchCarousel}/>
                                }

                                {this.shouldRenderRecentlyGigs() &&
                                <RecentlyViewedGigs isLoggedIn={isLoggedIn}
                                    isTouch={isTouch}
                                    shouldFetchCarousel={shouldFetchCarousel}/>
                                }

                            </div>
                        </aside>
                    }

                </div>
            </Provider>
        );
    }
}

GigPage.defaultProps = {
    sellerCard: {},
    studio: {},
    description: {},
    limitedEdition: {},
    rollouts: {}
};

GigPage.childContextTypes = {
    biEvents: object,
    logger: object,
    general: object,
    currencyConverter: object,
    pathfinderWrapper: object,
    choiceEligibilities: array,
    limitedEdition: object,
    rollouts: object,
    currentUser: object
};

export default GigPage;
